/**
 * HOW FAR UP THE CLOSED CHAT BAR REACHES (Miles, 2026-10-07: "when user scrolls all the way down the bottom center bar
 * covers over the footer and shouldn't, there should be at a gap at the very very bottom of the page so the user can
 * see the full footer when scrolled all the way down").
 *
 * The closed bar (and a member's chat bubble on a phone) is fixed over the foot of the page. The chat publishes how far
 * up from the window's bottom edge it reaches, a gap included; each footer pads its own foot by at least that much, so
 * at the very bottom of a page every line of the footer sits above the bar. Nothing is written on <html> or <body>:
 * each footer sets its own padding, so a change restyles only that footer (an inherited value written on an ancestor of
 * the chat's transcript costs seconds of restyle: tests/hooks/aLayoutVariableStaysOnThePane.test.ts).
 *
 * The chat attaches `chatBarRef` (one stable function, so React calls it only when the bar or the bubble mounts or
 * goes) to whichever of the two it draws. The value is 0 while neither is on the page: the chat open, a page the chat
 * stays off, or before the chat has loaded. It changes only when the bar's own size does, or the window's.
 *
 * Imports only React, so the first download, the shell's chunk and the chat engine can all read it.
 */
import { useSyncExternalStore } from 'react';

/** The space kept between the bar's top and the footer's last line. */
export const CHAT_BAR_GAP_PX = 20;

let reach = 0;
let bar: HTMLElement | null = null;
let observer: ResizeObserver | null = null;
const listeners = new Set<() => void>();

/** The bar's top: its own box, or the disclosure line that floats above it, whichever is higher. */
function topOf(el: HTMLElement): number {
  let top = el.getBoundingClientRect().top;
  const line = el.querySelector('[data-guest-bot-disclosure]');
  if (line) {
    const r = line.getBoundingClientRect();
    if (r.height > 0) top = Math.min(top, r.top);
  }
  return top;
}

function publish(next: number): void {
  if (next === reach) return;
  reach = next;
  listeners.forEach((l) => l());
}

/** Measure the bar now (a resize of the bar or the window calls it). */
export function measureChatBar(): void {
  const el = bar;
  if (!el || !el.isConnected || typeof window === 'undefined') {
    publish(0);
    return;
  }
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) {
    publish(0);
    return;
  }
  publish(Math.max(0, Math.round(window.innerHeight - topOf(el) + CHAT_BAR_GAP_PX)));
}

const onResize = () => measureChatBar();

/** The chat's ref for the closed bar and the phone's bubble. Stable: pass it as is, never wrapped in a new function. */
export function chatBarRef(el: HTMLElement | null): void {
  if (el === bar) return;
  observer?.disconnect();
  observer = null;
  if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
  bar = el;
  if (el && typeof window !== 'undefined') {
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(onResize);
      observer.observe(el);
      const line = el.querySelector('[data-guest-bot-disclosure]');
      if (line) observer.observe(line);
    }
    window.addEventListener('resize', onResize);
  }
  measureChatBar();
}

export function getChatBarReach(): number {
  return reach;
}

export function subscribeChatBarReach(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** How far up the closed bar reaches, in px (0 when there is none). */
export function useChatBarReach(): number {
  return useSyncExternalStore(subscribeChatBarReach, getChatBarReach, () => 0);
}

/** Tests only: forget the bar. */
export function resetChatBarReachForTests(): void {
  chatBarRef(null);
  listeners.clear();
  reach = 0;
}
